import { BaseButton } from '@/components/common/base-button';
import { ContentLoading } from '@/components/common/content-loading';
import EventPageCount from '@/components/feature/event/event-page-count';
import EventSlideNavigationButton from '@/components/feature/event/event-slide-navigation-button';
import { AlertIcon, ArrowLeftIcon } from '@/components/icons/event';
import { EVENT_LIST_PAGE_SIZE, EVENT_LIST_PAGE_SIZE_HALF, getEventEntryCategoryByCode } from '@/constants/event-categories';
import { useAccessibilityTTS, useTickSoundInteraction } from '@/hooks';
import { useGlobalCountdownReset } from '@/hooks/useGlobalCountdownReset';
import { useOffEvtListQuery } from '@/services/queries/event';
import { useConfigStore } from '@/stores';
import type { EventBenefitCategoryCode, EventListItem } from '@/types/event';
import { filterEventsByCategoryAndLanguage, mapDutyFreeEvents, paginateEvents } from '@/utils/event';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Swiper as SwiperType } from 'swiper';
import { A11y, Virtual } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import EventListCard from './EventListCard';
import styles from './styles.module.scss';
import 'swiper/css';

interface EventListViewProps {
    categoryCode: EventBenefitCategoryCode;
    onSelectEvent: (item: EventListItem) => void;
    onBack: () => void;
    initialPageIndex?: number;
    onPageChange?: (pageIndex: number) => void;
}

export default function EventListView({ categoryCode, onSelectEvent, onBack, initialPageIndex = 0, onPageChange }: EventListViewProps) {
    const { t } = useTranslation();
    const { speak } = useAccessibilityTTS();
    const { playTick } = useTickSoundInteraction();
    const { resetCountdown } = useGlobalCountdownReset();
    const voice = useConfigStore((state) => state.config.tts.voice);
    const contrast = useConfigStore((state) => state.config.ui.contrast);
    const language = useConfigStore((state) => state.config.ui.language);
    const screenMode = useConfigStore((state) => state.config.ui.screenMode);
    const isHalfScreen = screenMode === 'split';
    const isZoomIn = useConfigStore((state) => state.config.ui.zoomIn);
    const isZoomClosed = useConfigStore((state) => state.config.ui.zoomClosed);
    const storeCode = useConfigStore((state) => state.config.server?.store?.storeCode ?? null);

    const { data, isLoading, isError } = useOffEvtListQuery();

    const isZoomed = useMemo(() => isZoomIn || (isZoomClosed === false && isZoomIn === false), [isZoomIn, isZoomClosed]);
    const pageSize = isHalfScreen ? EVENT_LIST_PAGE_SIZE_HALF : EVENT_LIST_PAGE_SIZE;

    const [currentSlideIndex, setCurrentSlideIndex] = useState(initialPageIndex);
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd] = useState(false);

    const swiperRef = useRef<SwiperType | null>(null);
    const categoryBannerRef = useRef<HTMLDivElement>(null);
    const prevButtonRef = useRef<HTMLButtonElement>(null);
    const nextButtonRef = useRef<HTMLButtonElement>(null);
    const prevExecutingRef = useRef(false);
    const nextExecutingRef = useRef(false);

    const category = useMemo(() => getEventEntryCategoryByCode(categoryCode), [categoryCode]);
    const categoryTitle = category ? t(category.titleKey) : '';
    const categorySubtitle = category ? t(category.subtitleKey) : '';

    const items = useMemo(() => {
        const mapped = mapDutyFreeEvents(data);
        return filterEventsByCategoryAndLanguage(mapped, categoryCode, language, storeCode);
    }, [data, categoryCode, language, storeCode]);

    const pages = useMemo(() => paginateEvents(items, pageSize), [items, pageSize]);

    useEffect(() => {
        if (swiperRef.current) {
            swiperRef.current.slideTo(initialPageIndex, 0);
            setCurrentSlideIndex(initialPageIndex);
        }
    }, [initialPageIndex, categoryCode, items.length]);

    useEffect(() => {
        if (swiperRef.current) {
            setCurrentSlideIndex(0);
            setIsBeginning(true);
            setIsEnd(pages.length <= 1);
            swiperRef.current.slideTo(0, 0);
        }
    }, [pages.length, categoryCode]);

    useEffect(() => {
        if (swiperRef.current) {
            swiperRef.current.allowTouchMove = true;
            swiperRef.current.allowSlideNext = !isZoomed;
            swiperRef.current.allowSlidePrev = !isZoomed;
        }
    }, [isZoomed]);

    useEffect(() => {
        if (isLoading || isHalfScreen) return;

        const timer = setTimeout(() => {
            categoryBannerRef.current?.focus();
        }, 100);

        return () => clearTimeout(timer);
    }, [categoryCode, isLoading, isHalfScreen]);

    const handleSlideChange = (swiper: SwiperType) => {
        setCurrentSlideIndex(swiper.activeIndex);
        setIsBeginning(swiper.isBeginning);
        setIsEnd(swiper.isEnd);
        onPageChange?.(swiper.activeIndex);
    };

    const prevButtonText = t('floor_guide.text_to_speech.back');
    const nextButtonText = t('floor_guide.text_to_speech.next');
    const inactiveButtonText = t('common.button.tts.inactive_button');
    const activeButtonText = t('common.button.tts.button');

    const handlePrevWithTTS = useCallback(() => {
        resetCountdown();
        if (prevExecutingRef.current) return;
        prevExecutingRef.current = true;

        if (swiperRef.current && !isBeginning) {
            if (isZoomed) {
                swiperRef.current.allowSlideNext = true;
                swiperRef.current.allowSlidePrev = true;
            }

            swiperRef.current.slidePrev();

            if (isZoomed) {
                setTimeout(() => {
                    if (swiperRef.current) {
                        swiperRef.current.allowSlideNext = false;
                        swiperRef.current.allowSlidePrev = false;
                    }
                }, 50);
            }

            if (voice) speak(t('event.slide.prev_btn_pressed', { page: currentSlideIndex }));
        } else if (isBeginning && voice) {
            speak(t('common.button.tts.inactive_previous_button'));
        }

        setTimeout(() => {
            prevExecutingRef.current = false;
        }, 100);
    }, [isBeginning, currentSlideIndex, voice, speak, t, isZoomed, resetCountdown]);

    const handleNextWithTTS = useCallback(() => {
        resetCountdown();
        if (nextExecutingRef.current) return;
        nextExecutingRef.current = true;

        if (swiperRef.current && !isEnd) {
            if (isZoomed) {
                swiperRef.current.allowSlideNext = true;
                swiperRef.current.allowSlidePrev = true;
            }

            swiperRef.current.slideNext();

            if (isZoomed) {
                setTimeout(() => {
                    if (swiperRef.current) {
                        swiperRef.current.allowSlideNext = false;
                        swiperRef.current.allowSlidePrev = false;
                    }
                }, 50);
            }

            if (voice) speak(t('event.slide.next_btn_pressed', { page: currentSlideIndex + 2 }));
        } else if (isEnd && voice) {
            speak(t('common.button.tts.inactive_next_button'));
        }

        setTimeout(() => {
            nextExecutingRef.current = false;
        }, 100);
    }, [isEnd, currentSlideIndex, voice, speak, t, isZoomed, resetCountdown]);

    const showPagination = pages.length > 1;

    // Spec order after page header(1): category(2) → page(3) → cards(3) → prev(4) → next(5) → back(6)
    const categoryTabIndex = 2;
    const pageCountTabIndex = isHalfScreen ? 2 : 3;
    const cardsTabIndex = 3;
    const prevTabIndex = 4;
    const nextTabIndex = 5;
    const backTabIndex = 6;

    const handleBack = useCallback(() => {
        if (voice) speak(t('event.button.back_to_main_view_pressed'));
        onBack();
    }, [voice, speak, t, onBack]);

    const renderCategoryBanner = () => {
        if (isHalfScreen) return null;

        return (
            <div
                ref={categoryBannerRef}
                className={clsx(styles.categoryBanner, 'focus-border')}
                data-region-nav
                data-region-order={categoryTabIndex}
                data-interactive
                tabIndex={categoryTabIndex}
                aria-label={`${categoryTitle} ${categorySubtitle}`.trim()}
            >
                <p className={styles.categoryBanner__title}>{categoryTitle}</p>
                {categorySubtitle && <p className={styles.categoryBanner__subtitle}>{categorySubtitle}</p>}
            </div>
        );
    };

    const renderBackButton = () => (
        <div className={clsx(styles.actions, isHalfScreen && styles['actions--half'])}>
            <BaseButton
                role="button"
                type="button"
                onClick={handleBack}
                className={clsx(styles.backBtn, 'focus-border', isHalfScreen && styles['backBtn--half'])}
                aria-label={t('event.button.back_to_main_view')}
                tabIndex={backTabIndex}
                data-interactive
                data-type="button"
                data-role={activeButtonText}
                data-region-nav
                data-region-order={backTabIndex}
            >
                <ArrowLeftIcon />
                {t('event.button.back_to_main_view')}
            </BaseButton>
        </div>
    );

    const mainClassName = clsx(styles.main, isHalfScreen && styles['main--half']);

    if (isLoading) {
        return (
            <main className={mainClassName} data-nav="content">
                <ContentLoading loading />
            </main>
        );
    }

    if (isError) {
        return (
            <main className={mainClassName} data-nav="content">
                {renderCategoryBanner()}
                <p className={styles.status}>{t('event.load_error')}</p>
                {renderBackButton()}
            </main>
        );
    }

    if (items.length === 0) {
        return (
            <main className={mainClassName} data-nav="content">
                {renderCategoryBanner()}
                <div
                    className={clsx(styles.empty, 'focus-border')}
                    data-region-nav
                    data-region-order={2}
                    tabIndex={2}
                    data-interactive
                    aria-label={t('event.no_events')}
                >
                    <AlertIcon className={styles.empty__icon} aria-hidden />
                    <p className={styles.empty__text}>{t('event.no_events')}</p>
                </div>
                {renderBackButton()}
            </main>
        );
    }

    return (
        <main className={mainClassName} data-nav="content">
            {renderCategoryBanner()}
            <div className={styles.gridSection}>
                {showPagination && (
                    <div className={clsx(styles.pagination, isHalfScreen && styles['pagination--half'])}>
                        <EventPageCount
                            contrast={contrast}
                            page={currentSlideIndex + 1}
                            totalPage={pages.length}
                            tabIndex={pageCountTabIndex}
                        />
                    </div>
                )}

                <div className={styles.swiperWrap}>
                    <Swiper
                        modules={[Virtual, A11y]}
                        allowSlideNext={!isZoomed}
                        allowSlidePrev={!isZoomed}
                        allowTouchMove
                        virtual={{ enabled: true, addSlidesBefore: 1, addSlidesAfter: 1 }}
                        spaceBetween={0}
                        slidesPerView={1}
                        threshold={isZoomed ? 1000 : 5}
                        resistance
                        resistanceRatio={isZoomed ? 1 : 0.85}
                        shortSwipes={!isZoomed}
                        longSwipes={!isZoomed}
                        onSwiper={(swiper) => {
                            swiperRef.current = swiper;
                        }}
                        onSlideChange={handleSlideChange}
                    >
                        {pages.map((pageItems, slideIndex) => {
                            const isCurrentSlide = slideIndex === currentSlideIndex;
                            return (
                                <SwiperSlide key={slideIndex}>
                                    <div
                                        className={clsx(styles.grid, isHalfScreen && styles['grid--half'])}
                                        style={{
                                            overflow: isZoomed ? 'auto' : 'visible',
                                            pointerEvents: 'auto',
                                            touchAction: isZoomed ? 'pan-x pan-y' : 'auto',
                                        }}
                                        data-region-nav={isCurrentSlide || undefined}
                                        data-region-order={isCurrentSlide ? cardsTabIndex : undefined}
                                    >
                                        {pageItems.map((item) => (
                                            <EventListCard
                                                key={item.id}
                                                item={item}
                                                isHalfScreen={isHalfScreen}
                                                tabIndex={isCurrentSlide ? cardsTabIndex : -1}
                                                onClick={onSelectEvent}
                                            />
                                        ))}
                                    </div>
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>

                    {showPagination && (
                        <>
                            <EventSlideNavigationButton
                                ref={prevButtonRef as React.RefObject<HTMLButtonElement>}
                                data-region-nav
                                data-region-order={prevTabIndex}
                                data-tts-scope="carousel-navigation-button"
                                navigationDirection="previous"
                                aria-label={prevButtonText}
                                isDisabled={isBeginning}
                                tabIndex={prevTabIndex}
                                role="button"
                                data-role={t('common.data_role.button')}
                                data-interactive
                                className={styles.navPrev}
                                onFocus={() => {
                                    if (isBeginning) {
                                        speak(`${prevButtonText} ${inactiveButtonText}`);
                                    } else {
                                        speak(`${prevButtonText} ${activeButtonText}`);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        playTick();
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handlePrevWithTTS();
                                    }
                                }}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handlePrevWithTTS();
                                }}
                                onClick={() => {
                                    prevButtonRef.current?.focus();
                                }}
                            />
                            <EventSlideNavigationButton
                                ref={nextButtonRef as React.RefObject<HTMLButtonElement>}
                                data-region-nav
                                data-region-order={nextTabIndex}
                                data-tts-scope="carousel-navigation-button"
                                navigationDirection="next"
                                aria-label={nextButtonText}
                                isDisabled={isEnd}
                                tabIndex={nextTabIndex}
                                role="button"
                                data-role={t('common.data_role.button')}
                                data-interactive
                                className={styles.navNext}
                                onFocus={() => {
                                    if (isEnd) {
                                        speak(`${nextButtonText} ${inactiveButtonText}`);
                                    } else {
                                        speak(`${nextButtonText} ${activeButtonText}`);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        playTick();
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleNextWithTTS();
                                    }
                                }}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    handleNextWithTTS();
                                }}
                                onClick={() => {
                                    nextButtonRef.current?.focus();
                                }}
                            />
                        </>
                    )}
                </div>
            </div>

            {renderBackButton()}
        </main>
    );
}
